<template>
    <div class="w-full">
        <label for="email" class="block mb-4">Email</label>
        <div class="grid w-full flex flex-wrap col-12">
            <div class=" col-12 lg:col-6 mb-4 ">
                <InputText id="email" v-model="localEmail" class="w-full" variant="filled" size="small" disabled />
            </div>
            <div class="col-12 lg:col-6 p-d-flex ">
                <Button icon="pi pi-pen-to-square" aria-label="Save" size="small" :class="ConfirmationBtn"
                    @click="requireConfirmation()" :disabled="apiCallInProgress" />
            </div>
        </div>


        <ConfirmDialog group="current_email_confirmation">
            <template #container="{ message, rejectCallback }">
                <div v-if="!verificationsDone">
                    <!-- Verify current email address -->
                    <div class="p-4 text-center" v-if="!oldEmailVerified">
                        <h2 class="font-bold text-lg mb-3">{{ message.header }}</h2>
                        <p>
                            We've sent a verification code to
                            <br />
                            <b>{{ props.profile.email }}</b>.
                        </p>
                        <p>Please enter it here.</p>
                        <div class="card flex justify-center justify-content-center">
                            <Divider />
                            <InputOtp v-model="otp" variant="filled" :length="6" />
                            <Divider />
                        </div>
                        <div class="mt-6 flex justify-center gap-4 justify-content-center">
                            <Button label="Verify" @click="verifyOTPCode" class="teal-button" size="small" />
                             <Button label="Cancel" @click="rejectCallback" severity="danger" variant="text"
                                size="small" />
                        </div>
                        <!-- <div class="flex justify-center gap-4 justify-content-center">
                            <Button label="Cancel" @click="rejectCallback" severity="danger" variant="text"
                                size="small" />
                        </div> -->
                        <Divider />
                        <div class="flex justify-center gap-4 justify-content-center">
                            <Button label="Resend Code" @click="resendCode" severity="secondary" variant="text"
                                size="small" :loading="resendingCode" />
                        </div>
                        <div class="flex justify-center gap-4 justify-content-center">
                            <Button label="I don't have access to this email" @click="rejectCallback"
                                severity="contrast" variant="text" size="small" />
                        </div>
                    </div>

                    <!-- Verify new email address -->
                    <div class="p-4 text-center" v-if="oldEmailVerified">
                        <div v-if="!newEmailVerificationSend">
                            <h2 class="font-bold text-lg mb-3">Let's setup your new email</h2>
                            <p>Please enter your new email address below. <br />We will send you a verification code to
                                confirm
                                it.
                            </p>

                            <div class="flex justify-center gap-4 justify-content-center">
                                <InputText id="email" v-model="newEmail" class="w-full" variant="filled" size="small" />
                            </div>
                            <div class="mt-1 flex justify-center gap-4 justify-content-center">
                                <Button label="Verify" @click="resendNewEmailVerificationCode" :class="ConfirmationBtn"
                                    size="small" :loading="resendingCode" />
                            </div>
                        </div>
                        <div v-else>
                            <p>Please enter verification code we sent to <br /> <b>{{ newEmail }}</b>.</p>
                            <div class="card flex justify-center justify-content-center">
                                <Divider />
                                <InputOtp v-model="otp" variant="filled" :length="6" />
                                <Divider />
                            </div>
                            <div class="mt-6 flex justify-center gap-4 justify-content-center">
                                <Button label="Verify" @click="verifyOTPCode" :class="ConfirmationBtn" size="small" />
                            </div>
                            <Divider />
                            <div class="flex justify-center gap-4 justify-content-center">
                                <Button label="Resend Code"
                                    @click="newEmailVerificationSend ? resendNewEmailVerificationCode : resendCode"
                                    severity="secondary" variant="text" size="small" :loading="resendingCode" />
                            </div>
                        </div>
                        <div class="flex justify-center gap-4 justify-content-center">
                            <Button label="Cancel" @click="rejectCallback" severity="danger" variant="text"
                                size="small" />
                        </div>
                    </div>
                </div>

                <div class="p-4 text-center" v-else>
                    <div v-if="!updateDone">
                        <h2 class="font-bold text-lg mb-3">Updating Your Account</h2>
                        <p>This may take a few seconds...<br /><b>Please don't close this page.</b></p>
                        <ProgressBar mode="indeterminate" style="height: 6px"></ProgressBar>
                    </div>
                    <div v-else>
                        <h2 class="font-bold text-lg mb-3">Done!</h2>
                        <p>Your account email has been updated.</p><br />
                        <p>We recommend you to sign out and sign in again to make sure everything is fine.</p><br />
                        <div class="flex justify-center gap-4 justify-content-center">
                            <Button label="Done" @click="rejectCallback" severity="contrast" variant="text"
                                size="small" />
                        </div>
                        <div class="flex justify-center gap-4 justify-content-center">
                            <Button label="Sign Out" @click="signOut" severity="danger" variant="text" size="small" />
                        </div>
                    </div>
                </div>
            </template>
        </ConfirmDialog>
    </div>
</template>

<script setup>
import ConfirmDialog from 'primevue/confirmdialog';
import { Button, Divider, InputOtp, InputText } from 'primevue';
import ProgressBar from 'primevue/progressbar';
import { ref, defineProps, computed } from "vue";
import * as authApi from "@/api/auth";
import * as studentApi from "@/api/student";
import * as tutorApi from "@/api/tutor";
import { notifyContrast, notifyError } from '@/utils/notification';
import { useAuthStore } from "@/stores/authStore";
import { useRouter } from "vue-router";
import { useConfirm } from "primevue/useconfirm";

const props = defineProps({
    profile: {
        type: Object,
        required: true
    }
});
const localEmail = ref(props.profile.email);
const confirm = useConfirm();
const router = useRouter();
const authStore = useAuthStore();
const otp = ref("");
const oldEmailVerified = ref(false);
const newEmailVerificationSend = ref(false);
const verificationsDone = ref(false);
const newEmail = ref("");
const updateDone = ref(false);
const apiCallInProgress = ref(false);
const resendingCode = ref(false);


const requireConfirmation = () => {
    const payload = {
        email: props.profile.email,
        verifingOldEmail: true
    }
    sendOtp(payload);
    confirm.require({
        group: 'current_email_confirmation',
        header: 'Please confirm!',
    });
};

async function sendOtp(data) {
    resendingCode.value = true;
    await authApi.verifyEmailBeforeChange(data);
    const email = data.verifingNewEmail ? data.newEmail : data.email;
    notifyContrast("Verification Email Sent", "Please check \n" + email + "\nfor the verification code.");
    resendingCode.value = false;
}

function resendCode() {
    const payload = {
        email: props.profile.email,
        verifingOldEmail: true
    }
    sendOtp(payload);
}

function resendNewEmailVerificationCode() {
    const payload = {
        email: props.profile.email,
        newEmail: newEmail.value,
        verifingNewEmail: true
    }
    sendOtp(payload);
    newEmailVerificationSend.value = true;
}
async function verifyOTPCode() {
    try {
        await authApi.verifyResetToken({ email: props.profile.email, code: otp.value });
        oldEmailVerified.value = true;
        otp.value = "";
        if (newEmailVerificationSend.value) {
            verificationsDone.value = true;
            updateAccountEmail();
        }
    } catch {
        notifyError("Invalid Code!", "Please try again and make sure the code is correct.");
    }
}

async function updateAccountEmail() {
    if (props.profile.role === "student") {
        await studentApi.updateEmail({ email: newEmail.value });
        const updated = props.profile;
        updated.email = newEmail.value;
        authStore.updateLocalUser(updated);
        updateDone.value = true;
    } else if (props.profile.role === "tutor") {
        await tutorApi.updateEmail({ email: newEmail.value });
        const updated = props.profile;
        updated.email = newEmail.value;
        authStore.updateLocalUser(updated);
        updateDone.value = true;

    }
}

function signOut() {
    authStore.logout();
    router.push('/login');
}
const ConfirmationBtn = computed(() => {
    switch (props.profile.role) {
        case 'student':
            return 'orange-button';
        case 'tutor':
            return 'teal-button';
        case 'institute':
            return 'yellow-button';
        default:
            return 'teal-button';
    }
});
</script>